/**
 * ResourceFlowControls - Панель параметров виджета «Движение ресурсов»
 * ======================================================================
 *
 * Выделена в отдельный файл, чтобы не раздувать ResourceFlowWidget.js.
 * Платформа и диапазон дат берутся из глобальных фильтров дашборда (сайдбар);
 * диапазон трактуется как когорта регистрации (acc_reg_ts), как в ретеншене
 * и прогрессии. Локальный параметр виджета — только гео.
 */

import React from 'react';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { resources as resourcesSpecs } from '@ggstat/chart-specs';

export const AXIS_OPTIONS = resourcesSpecs.flow.axisOptions;

export const METRIC_OPTIONS = resourcesSpecs.flow.metricOptions;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });

export function ResourceFlowControls({
  resource,
  onResourceChange,
  resourceOptions,
  axis,
  onAxisChange,
  metric,
  onMetricChange,
  includeCheaters,
  onIncludeCheatersChange,
  showBand,
  onShowBandChange,
  geo,
  onGeoChange
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Ресурс</Label>
          <Select value={resource} onValueChange={onResourceChange}>
            <SelectTrigger className="w-[220px] h-9 text-xs">
              <SelectValue placeholder="Ресурс" />
            </SelectTrigger>
            <SelectContent>
              {resourceOptions.map((item) => (
                <SelectItem key={item.name} value={item.name}>
                  {item.name} · {fmtInt(item.players)} игроков
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Гео (опц.)</Label>
          <Input
            type="text"
            value={geo}
            onChange={(e) => onGeoChange(e.target.value)}
            placeholder="US, RU, ..."
            className="h-9 w-[110px] text-xs"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Ось</span>
          <ToggleGroup type="single" value={axis} onValueChange={(v) => v && onAxisChange(v)}>
            {AXIS_OPTIONS.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} aria-label={opt.label}>
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Метрика</span>
          <ToggleGroup type="single" value={metric} onValueChange={(v) => v && onMetricChange(v)}>
            {METRIC_OPTIONS.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} aria-label={opt.label}>
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="resource-flow-cheaters"
            checked={includeCheaters}
            onCheckedChange={(checked) => onIncludeCheatersChange(Boolean(checked))}
          />
          <Label htmlFor="resource-flow-cheaters" className="text-xs cursor-pointer">
            Показать читеров
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="resource-flow-band"
            checked={showBand}
            onCheckedChange={(checked) => onShowBandChange(Boolean(checked))}
          />
          <Label htmlFor="resource-flow-band" className="text-xs cursor-pointer">
            Коридор P25–P75
          </Label>
        </div>
      </div>
    </div>
  );
}

export default ResourceFlowControls;
